import {
    getInvalidItemFields,
    ITEM_BASE_FIELDS,
    ITEM_DIMENSION_FIELDS,
    ITEM_MARKETPLACE_FIELDS,
} from '@onward-delivery/core';

/**
 * Validation errors for one item. Shared by the modal-level aggregate (isValid / hasErrorTab) and
 * by each item row, so a row derives its own errors instead of taking a per-keystroke prop.
 *
 * @param {object} item The item to check.
 * @param {{ oms: boolean, freightType: string, sourceForm: string, assemblyRequired: boolean }} order
 *   Order fields that change which item fields are required.
 * @returns {Object<string, true>} Invalid field names mapped to `true`; empty when valid.
 */
export const getItemErrors = (item, { oms, freightType, sourceForm, assemblyRequired }) => {
    const invalidFields = new Set(
        getInvalidItemFields(item, {
            oms,
            freight_type: freightType,
            assembly_required: assemblyRequired,
        })
    );
    const isEmpty = (attr) => !item[attr] || item[attr].length === 0;
    const setFieldError = (field, hasError) => {
        if (hasError) invalidFields.add(field);
        else invalidFields.delete(field);
    };

    invalidFields.delete('quantity');

    ITEM_BASE_FIELDS.filter((field) => field !== 'quantity').forEach((field) => {
        setFieldError(field, isEmpty(field));
    });

    ITEM_DIMENSION_FIELDS.forEach((field) => {
        setFieldError(field, isEmpty(field) && isEmpty('cubes_per_unit'));
    });
    setFieldError(
        'cubes_per_unit',
        isEmpty('cubes_per_unit') && ITEM_DIMENSION_FIELDS.every((field) => isEmpty(field))
    );

    const requiresMarketplaceFields = !oms && freightType === 'household';
    ITEM_MARKETPLACE_FIELDS.forEach((field) => setFieldError(field, requiresMarketplaceFields && isEmpty(field)));

    const requiresAssembly = requiresMarketplaceFields || assemblyRequired;
    const hasAssemblyError = isEmpty('assembly_type') || (assemblyRequired && item.assembly_type === 'no_assembly');
    setFieldError('assembly_type', requiresAssembly && hasAssemblyError);

    const requiresPieceCount = !oms && sourceForm === 'CUSTOMER';
    setFieldError('item_piece_count', requiresPieceCount && isEmpty('item_piece_count'));

    return Object.fromEntries([...invalidFields].map((field) => [field, true]));
};
